import { useMemo, useRef, useState } from "react";
import { CompanyFieldDetailsDto } from "@advanced-sales-technologies/backend-client/models/CompanyFieldDetailsDto";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";

import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";
import { CompanyDetailLink } from "@/components/molecules/company/detail/overview/basic/CompanyDetailLinks";
import { ModalCloseProps } from "@/components/molecules/Modal";
import SelectAsyncComboBox from "@/components/organisms/SelectAsyncComboBox";

export type CompanyDetailLinkAsync = {
  linkType: "website" | "phone" | "email";
  infiniteQuery: (query: string) => UseInfiniteQueryResult<InfiniteData<CompanyFieldDetailsDto, unknown>, Error>;
  featuredIcon?: React.ReactNode;
  heading: string;
  placeholder: string;
};

type Props = CompanyDetailLinkAsync & ModalCloseProps;

const CompanyDetailMoreData = ({
  linkType,
  preventClose,
  onClose,
  infiniteQuery,
  heading,
  featuredIcon,
  placeholder,
}: Props) => {
  const [query, setQuery] = useState<string>("");
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = infiniteQuery(
    query?.trim() === "" ? "" : query,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const observerRef = useInfiniteScroll({ hasNextPage, fetchNextPage, containerRef });

  const flatData = useMemo(() => data?.pages.flatMap((page) => page.data), [data]);

  return (
    <BasicModalContent
      featuredIcon={featuredIcon}
      heading={heading}
      preventClose={preventClose}
      onClose={onClose}
      data-testid={`company-detail-more`}
    >
      <SelectAsyncComboBox
        options={flatData || []}
        placeholder={placeholder}
        optionsClassName={"flex flex-col h-96"}
        renderOption={({ id, value: href }) => (
          <div className={"border-b border-b-secondary py-3"}>
            <CompanyDetailLink key={id} href={href} type={linkType}>
              {href}
            </CompanyDetailLink>
          </div>
        )}
        isLoading={isLoading}
        onQueryUpdate={setQuery}
        observerRef={observerRef}
        containerRef={containerRef}
        isFetchingNextPage={isFetchingNextPage}
        renderLoading={Array.from({ length: 10 }).map((_, index) => (
          <div key={index} className={"border-b border-b-secondary py-3"}>
            <div className={"h-3 w-1/3 animate-pulse rounded-full bg-gray-300"} />
          </div>
        ))}
      />
    </BasicModalContent>
  );
};

export default CompanyDetailMoreData;
